import { createApi } from '@reduxjs/toolkit/query/react';

import { authQuery } from './baseQuery';
import type { TableResponse, PaginatedResponse } from 'src/types/common';
import {
  type IReport,
  type ReportFormat,
  type IOnDemandExport,
  type IReportStatistics,
  type IReportKey,
  ReportStatus,
} from 'src/types/reports';
import { withUtcIsoDateRangeState } from 'src/utils/date-picker';
import type { IPaginationState } from 'src/utils/server-side-pagination';
import { convertObjToUrl } from 'src/utils/server-side-pagination';

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const reportsAPISlice = createApi({
  reducerPath: 'reports-api',
  tagTypes: ['Reports'],
  baseQuery: authQuery,
  endpoints: (builder) => ({
    getAllReports: builder.query<TableResponse<IReport>, void>({
      query: () => '/reports?page=1&limit=5000&sort=createdAt&order=desc',
      transformResponse: (res: TableResponse<IReport>) => {
        res.rows = res.rows
          .filter((el) => el.fileType)
          .map((el) => ({
            ...el,
            fileSize: el.fileSize ? formatBytes(parseInt(el.fileSize, 10)) : '0 Bytes',
            fileType: el.fileType.toLowerCase() as ReportFormat,
          }));

        return res;
      },
      providesTags: ['Reports'],
    }),
    getReports: builder.query<
      PaginatedResponse<IReport>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ state, limit }) =>
        `/reports?limit=${limit}${convertObjToUrl(withUtcIsoDateRangeState(state))}`,
      providesTags: ['Reports'],
    }),
    getReportStatistics: builder.query<IReportStatistics, void>({
      query: () => `/reports/statistics`,
      // providesTags: ['Reports'],
    }),
    deleteReport: builder.mutation({
      query: (requestData) => ({
        url: '/payments/update-status', // THIS IS FOR TESTING PURPOSES // POSSIBLY DO OPTIMISTIC UPDATE
        method: 'POST',
        body: requestData,
      }),
      // invalidatesTags: ['Reports'],
      async onQueryStarted({ payments }, lifecycleApi) {
        const getPostsPatchResult = lifecycleApi.dispatch(
          reportsAPISlice.util.updateQueryData('getAllReports', undefined, (draft) => {
            draft.rows = draft.rows.filter((el) => el.id !== payments[0].id);
          })
        );

        try {
          await lifecycleApi.queryFulfilled;
        } catch {
          getPostsPatchResult.undo();
        }
      },
    }),
    getGeneratedExport: builder.query<IOnDemandExport, { exportId: string }>({
      query: ({ exportId }) => `reports/${exportId}/get-on-demand-export`,
    }),
    // ==========================================================
    // Generate report
    // ==========================================================
    generateReport: builder.mutation<
      { id: string },
      Pick<IReport, IReportKey.type | IReportKey.reportPeriod> & {
        clientId: string;
      }
    >({
      query: (requestData) => ({
        url: '/reports/generate',
        method: 'POST',
        body: requestData,
      }),
    }),
    // ==========================================================
    // Get generated report
    // ==========================================================
    getGeneratedReportOnDemand: builder.query<IReport | null, { id: string; empty?: boolean }>({
      query: ({ id }) => `/reports/get-generated-report/${id}`,
    }),
  }),
});

export const {
  useGetAllReportsQuery,
  useDeleteReportMutation,
  useGetReportsQuery,
  useGetReportStatisticsQuery,
  useGetGeneratedExportQuery,
  useGenerateReportMutation,
  useGetGeneratedReportOnDemandQuery,
} = reportsAPISlice;
